import type { ClientError } from '@anchorage/sentry';
import {
  WrappedError,
  handleError,
  isHtmlResponseError,
  notifyError,
} from '@anchorage/sentry';
import type { DefaultOptions, WatchQueryFetchPolicy } from '@apollo/client';
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import {
  KEEP,
  removeTypenameFromVariables,
} from '@apollo/client/link/remove-typename';
import { RetryLink } from '@apollo/client/link/retry';
import type { GraphQLFormattedError } from 'graphql';
import { OperationTypeNode } from 'graphql';

import fragmentMatcher from '../../generated/fragment-matcher';
import { isUnauthenticatedError } from '../apollo-errors';
import { typePolicies } from '../cache/typePolicies';
import { markSchemaMismatch } from './schemaMismatch';

// A GRAPHQL_VALIDATION_FAILED failure ships as an HTTP 400, which Apollo v4's
// HttpLink treats as a transport-layer ServerError (this `result.errors`
// shape, not CombinedGraphQLErrors) even though the body is valid GraphQL —
// see packages/sentry's gqlValidationFingerprint for the same shape check.
type ServerErrorLike = {
  result?: {
    errors?: { extensions?: { code?: unknown } }[];
  };
};

const hasSchemaValidationFailure = (error: Error): boolean => {
  const errors = (error as ServerErrorLike).result?.errors;
  return (
    Array.isArray(errors) &&
    errors.some(
      (gqlError) => gqlError?.extensions?.code === 'GRAPHQL_VALIDATION_FAILED',
    )
  );
};

export type ApolloClientConfig = {
  /** Base URI for GraphQL endpoint. Default: '/graphql' */
  uri?: string;
  /** Additional headers to include in requests */
  headers?: Record<string, string>;
  /** Enable Apollo DevTools. Default: true in development */
  devTools?: boolean;
  /** Additional Apollo links to insert before the terminating link */
  additionalLinks?: ApolloLink[];
  /** Custom network error handler that fully replaces the default.
   * When provided, `onUnauthenticated` has no effect (handle 401s yourself).
   * If not provided, uses a default that ignores server disconnect statuses
   * (499, 502, 503, 504) and "Failed to fetch" errors, and reports all others to Sentry.
   * `operationName` identifies the failed request — a network error carries no
   * GraphQL body, so it is the only thing that does. The mutation toast is not
   * part of this handler; the error link raises it either way. */
  onNetworkError?: (error: Error, operationName?: string) => void;
  /** Called on a 401. Receives the raw response when the error carries one:
   * an expired session and a policy denial are both 401, so only headers
   * separate them. Return `false` to leave the 401 to the default handling. */
  onUnauthenticated?: (response?: Response) => boolean | void;
  /** Default watchQuery fetch policy. Default: 'network-only' */
  fetchPolicy?: WatchQueryFetchPolicy;
  /** Identifies this app to Apollo Server/Studio and Datadog APM via the
   * `apollographql-client-name` header (Apollo Client's `clientAwareness`
   * feature). One per FE app, e.g. 'frontoffice', 'backoffice'. */
  name?: string;
  /** Paired with `name` — sent as `apollographql-client-version`. Not the
   * Apollo Client library version. */
  version?: string;
};

// Server disconnect statuses, expected during deploys and restarts.
const DISCONNECT_STATUS_CODES = [499, 502, 503, 504];

const getStatusCode = (error: Error): number | undefined => {
  const status = (error as { statusCode?: unknown }).statusCode;
  return typeof status === 'number' ? status : undefined;
};

const reportNetworkError = (error: Error, operationName?: string) => {
  const status = getStatusCode(error);
  if (status !== undefined && DISCONNECT_STATUS_CODES.includes(status)) {
    return;
  }

  // Ignore network connectivity issues — user's internet is down or request blocked
  if (error.toString().includes('Failed to fetch')) {
    return;
  }

  handleError(error, operationName ? { operationName } : undefined);
};

// A network failure on a mutation doesn't tell us whether the write landed: a
// 504 during a deploy can arrive after the server committed it.
const UNKNOWN_OUTCOME_DESCRIPTION =
  "We couldn't confirm this went through. Check before retrying.";
const REJECTED_DESCRIPTION = "This didn't go through, so nothing changed.";

// Execution errors come back as HTTP 200 with a per-error code (see
// handleError), so a 4xx means the request was refused before any resolver
// ran. 499 is a disconnect status, so its outcome is unknown.
const isRejectedBeforeExecution = (error: Error): boolean => {
  const status = getStatusCode(error);
  return (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    !DISCONNECT_STATUS_CODES.includes(status)
  );
};

// Queries stay silent on network errors: background and polling traffic would
// toast on every deploy, and the caller has `error` for inline state. A
// mutation's caller usually relies on this toast (`onError: () => undefined`).
// Not routed through handleError: disconnect statuses and "Failed to fetch"
// never reach it, an app can replace the reporter, and it skips HTML bodies.
const notifyMutationNetworkError = (
  operation: ApolloLink.Operation,
  error: Error,
) => {
  const ctx = operation.getContext();
  if (
    operation.operationType !== OperationTypeNode.MUTATION ||
    ctx.skipToast ||
    !notifyError
  ) {
    return;
  }

  // Static title/variant/action overrides apply as in the GraphQL branch. A
  // factory doesn't: factories build copy from the server's message, and a
  // network error's message ("Failed to fetch") isn't meant for users.
  notifyError({
    ...(typeof ctx.toast === 'function' ? undefined : ctx.toast),
    description: isRejectedBeforeExecution(error)
      ? REJECTED_DESCRIPTION
      : UNKNOWN_OUTCOME_DESCRIPTION,
  });
};

// Shared error link — handles all GraphQL + network errors. The handler
// receives a single `error` value and discriminates using
// `CombinedGraphQLErrors.is(...)`. GraphQL errors toast for every operation
// type; network errors toast only for mutations (notifyMutationNetworkError).
const createErrorLink = (
  onNetworkError: (error: Error, operationName?: string) => void,
  onUnauthenticated?: ApolloClientConfig['onUnauthenticated'],
): ErrorLink => {
  return new ErrorLink(({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
      const ctx = operation.getContext();

      error.errors.forEach((graphqlError: GraphQLFormattedError) => {
        // GRAPHQL_VALIDATION_FAILED can only happen against a schema that no
        // longer matches this bundle's queries — it's proof the tab is stale,
        // not an ordinary server error. Reported to Sentry below as usual;
        // this just additionally lets a stale-tab banner offer a reload. The
        // far more common case — a non-2xx response body — never reaches
        // here; see hasSchemaValidationFailure below for that path.
        if (graphqlError.extensions?.code === 'GRAPHQL_VALIDATION_FAILED') {
          markSchemaMismatch();
        }

        // Auto-toast is the default for GraphQL errors. Components can:
        //   - `context: { skipToast: true }` — suppress the toast entirely.
        //   - `context: { skipErrorReport: true }` — suppress the Sentry
        //     report for this operation's GraphQL errors. For probes whose
        //     errors are expected and non-actionable (e.g. a permission check
        //     the BE answers with an error). Orthogonal to the toast — pair
        //     with `skipToast` to fully silence the operation.
        //   - `context: { toast: { title?, description?, variant?, action? } }`
        //     — static overrides. The `description` field is treated as a
        //     **fallback**: the server's `error.message` wins when present,
        //     and the static description is shown only when the server
        //     message is empty. This matches the pre-migration
        //     `description: error.message || 'fallback'` idiom that was used
        //     across the codebase. Other fields (title/variant/action) are
        //     hard overrides.
        //   - `context: { toast: (error) => ({...}) }` — dynamic factory when
        //     the toast contents depend on the error at runtime (callers can
        //     return any description, and the factory return value wins
        //     verbatim — no server-message merge).
        const isFactory = typeof ctx.toast === 'function';
        const resolvedToast = isFactory
          ? ctx.toast(graphqlError as ClientError)
          : ctx.toast;
        const toastConfig = ctx.skipToast
          ? undefined
          : {
              ...(resolvedToast ?? {}),
              // Description policy: factory return wins verbatim. For static
              // overrides, server `error.message` takes precedence; the
              // static value acts as a fallback when the server message is
              // empty. This preserves the old `error.message || 'fallback'`
              // semantics across migrated call sites without per-callsite
              // edits.
              description: isFactory
                ? resolvedToast?.description
                : graphqlError.message || resolvedToast?.description,
            };

        // `context.errorWrapperMessage` lets callers prepend a
        // human-readable label to the Sentry report (replacing the
        // pre-migration `handleError(new WrappedError('label', e))` pattern
        // inside an onError). Toast still uses the unwrapped error message.
        const errorForReport = ctx.errorWrapperMessage
          ? new WrappedError(
              ctx.errorWrapperMessage as string,
              graphqlError as unknown as Error,
            )
          : (graphqlError as unknown as ClientError);

        handleError(errorForReport, {
          operationName: operation.operationName,
          operationVariables: operation.variables,
          ...(toastConfig ? { toastConfig } : {}),
          ...(ctx.skipErrorReport ? { skipErrorReport: true } : {}),
        });
      });
      return;
    }

    // Non-GraphQL → network/protocol-layer error.
    if (error instanceof Error) {
      if (hasSchemaValidationFailure(error)) {
        markSchemaMismatch();
      }

      // Handled before the toast: the app's redirect or session banner already
      // tells the user. Forwarded, not interpreted: the app branches on
      // headers so this package never learns any app's routes.
      if (
        isUnauthenticatedError(error) &&
        onUnauthenticated &&
        onUnauthenticated((error as { response?: Response }).response) !== false
      ) {
        return;
      }

      // An HTML response body means a load balancer error page or similar, not
      // a GraphQL result. Wrap the original error in a non-destructive proxy so
      // the user-facing `.message` is friendlier without mutating the live
      // error or losing the structured fields (`name`, `statusCode`,
      // `bodyText`, `response`) that downstream handlers
      // (`reportNetworkError`, `handleError`'s HTML suppression) depend
      // on. Detection lives in `@anchorage/sentry` because `handleError` keys
      // its suppression off the same signal.
      let errorToReport: Error = error;
      if (isHtmlResponseError(error)) {
        errorToReport = Object.assign(
          Object.create(Object.getPrototypeOf(error) as object),
          error,
          { message: 'Network Error: Please try again' },
        );
        // `Object.assign` copies own *enumerable* properties, and `stack` is
        // non-enumerable — without this the proxy carries no stack at all
        // (`Object.create` doesn't generate one), so anything reporting it
        // groups on the message alone with no frames.
        Object.defineProperty(errorToReport, 'stack', {
          value: error.stack,
          configurable: true,
          writable: true,
        });
      }
      notifyMutationNetworkError(operation, error);
      onNetworkError(errorToReport, operation.operationName);
    }
  });
};

// Shared retry link — retries queries on network failures. Never mutations: a
// "Failed to fetch" can follow a request the server already received, so a
// retry risks a duplicate write, and the backoff would hold the caller for hours.
const createRetryLink = () =>
  new RetryLink({
    delay: {
      initial: 3000,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 15,
      retryIf: (error, operation) =>
        operation.operationType !== OperationTypeNode.MUTATION &&
        error.toString().includes('Failed to fetch'),
    },
  });

// URL editing link — appends the query name and operation type. Call sites
// sharing one document (e.g. a generic `Operations` query) are indistinguishable
// in the network tab; pass `context: { queryNameOverride: 'SomeLabel' }` to
// disambiguate. The operation type (query, mutation, subscription) lets the
// server's request record tell a read from a write without parsing the body,
// which it proxies unread.
// v4: `operation`/`forward` come from the ApolloLink request signature directly;
// no `NextLink` import needed.
const createUrlEditingLink = (uri: string) =>
  new ApolloLink((operation, forward) => {
    const queryNameOverride = operation.getContext().queryNameOverride as
      | string
      | undefined;
    const operationName =
      queryNameOverride || operation.operationName || 'UnnamedOperation';
    const separator = uri.includes('?') ? '&' : '?';
    const queryName = encodeURIComponent(operationName);

    operation.setContext(() => ({
      uri: `${uri}${separator}queryName=${queryName}&operationType=${operation.operationType}`,
    }));

    return forward(operation);
  });

// Strips `__typename` from outgoing variables so query results can be reused
// as mutation inputs without tripping the gateway's strict (Router 2.x) input
// validation (CEXP-21004).
//
// `except` keys match a variable's TOP-LEVEL declared type; nested freeform
// fields use the `{ TypeName: { field: KEEP } }` form. We except the only
// freeform-JSON *input* in the schema — `PrepareActionFromJSONFormInput.formData`
// (`JSON!`) — whose value may legitimately contain a `__typename` key that must
// not be stripped. (The `JSON`/`Map` scalars are otherwise output-only, so no
// other variable carries freeform JSON today.)
//
// MAINTENANCE: if a new operation passes a `JSON`/`Map` (or serialized-JSON
// `String`) value as a variable, add a field-level `except` entry for its input
// type here BEFORE enabling the strip for it — otherwise the key is silently
// removed.
const createRemoveTypenameLink = () =>
  removeTypenameFromVariables({
    except: {
      PrepareActionFromJSONFormInput: {
        formData: KEEP,
      },
    },
  });

const createCache = () =>
  new InMemoryCache({
    possibleTypes: fragmentMatcher.possibleTypes,
    typePolicies,
  });

export function createApolloClient(
  config: ApolloClientConfig = {},
): ApolloClient {
  const {
    devTools = process.env.NODE_ENV !== 'production',
    additionalLinks = [],
    onNetworkError,
    onUnauthenticated,
    fetchPolicy = 'network-only',
  } = config;

  const defaultOptions: DefaultOptions = {
    watchQuery: {
      // Every page reads fresh data by default. A call site overrides it only when stale cache data is acceptable,
      // or with no-cache when a response must not normalize into the shared cache.
      fetchPolicy,
      // Left at v4's default: a global false would also suppress loading:true
      // on useLazyQuery's first exec. Poll suppression belongs in
      // usePollingQuery/usePaginatedQuery, which derive it per query.
    },
  };

  const uri = config.uri ?? '/graphql';

  const httpLink = new HttpLink({
    uri,
    credentials: 'same-origin',
    headers: config.headers,
  });

  // Build link chain: retry → error → urlEditing → removeTypename → [additional] → http
  // removeTypename strips `__typename` from all outgoing variables — query
  // results carry it and input types reject it (CEXP-21004). It must run before
  // the terminating link so the stripped variables are what goes over the wire.
  const links: ApolloLink[] = [
    createRetryLink(),
    // A custom onNetworkError owns 401s, so onUnauthenticated only applies
    // alongside the default reporter.
    onNetworkError
      ? createErrorLink(onNetworkError)
      : createErrorLink(reportNetworkError, onUnauthenticated),
    createUrlEditingLink(uri),
    createRemoveTypenameLink(),
    ...additionalLinks,
    httpLink,
  ];

  return new ApolloClient({
    link: ApolloLink.from(links),
    cache: createCache(),
    devtools: { enabled: devTools },
    defaultOptions,
    ...(config.name && {
      clientAwareness: { name: config.name, version: config.version },
    }),
  });
}
